import React from 'react';
import { Card, CardContent, Typography, Chip, Button, Tooltip, Box } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';

function formatColumnValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(', ');
  }
  if (value === null || value === undefined) return '';
  return String(value);
}

function normalizedColumns(columns) {
  if (!columns || typeof columns !== 'object' || Array.isArray(columns)) return [];
  return Object.entries(columns)
    .map(([label, value]) => ({ label, value: formatColumnValue(value) }))
    .filter((column) => column.label && column.value);
}

export default function CompanyResultCard({
  name,
  industry,
  hq,
  headcount,
  description,
  enrichmentColumns = {},
}) {
  const columns = normalizedColumns(enrichmentColumns);

  return (
    <Card
      elevation={1}
      sx={{ minWidth: 220, maxWidth: 260, borderRadius: '8px', flexShrink: 0 }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {name}
        </Typography>

        {industry && (
          <Chip
            label={industry}
            size="small"
            sx={{ mt: 0.5, mb: 1, fontSize: '11px' }}
          />
        )}

        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
          <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {hq}
          </Typography>
        </Box>

        {headcount && (
          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {headcount}
            </Typography>
          </Box>
        )}

        {description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1,
            }}
          >
            {description}
          </Typography>
        )}

        {columns.length > 0 && (
          <Box mt={1} pt={1} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            {columns.map((column) => (
              <Box key={column.label} mb={0.75}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'primary.main', fontWeight: 700 }}
                >
                  {column.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', wordBreak: 'break-word' }}
                >
                  {column.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Tooltip title="Coming soon">
          <span>
            <Button size="small" variant="outlined" disabled fullWidth sx={{ mt: 0.5 }}>
              Save to Workspace
            </Button>
          </span>
        </Tooltip>
      </CardContent>
    </Card>
  );
}
